import React, { useEffect } from "react";
import { Button, Drawer } from "antd";
import CheckIn from "../components/CheckIn";
import ExtraOption from "../components/ExtraOptions";
import { CloseOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { 
  resetBookingDetails,
  updateBookingDetailsEditable
 } from "../../../reducer/bookingReducer";
import { CircularProgress } from "@mui/material";
import AddOnsContainer from "../components/AddOnsContainer";
import { bookingDetailsDrawerWidth } from "../../../utils/bookingDetailsHelper";
import useAllBookingFunction from "../../../hooks/bookingCustomHook/useAllBookingFunction";
import useGetMealPlanRate from "../../../hooks/bookingCustomHook/useGetPlanRate";
import EditCompanyModal from "../Modal/EditCompanyModal";
import Footer from "../components/Footer";
import { getPartyService, postPartyService } from "../../../services/party";
import { modifyBooking } from "../../../services/booking";
import { partyObj } from "../../party/utils/utils";

const MainBookingDetailsDrawer = () => {
  const dispatch = useDispatch();

  const {
    showMainBookingDetailsDrawer,
    bookingDetails,
    loadingDetails,
    isBookingAlterationAllowed,
  } = useSelector((store) => store.booking);
  const [editCompanyModalOpen, setEditCompanyModalOpen] = React.useState(false);

  const { hotelDetails } = useSelector((store) => store.login);
  const roomTypes = hotelDetails?.roomTypes;
  const bookingType = showMainBookingDetailsDrawer?.bookingType;

  const { handleFolio, handleGrc } = useAllBookingFunction();

  const handleClose = () => {
    dispatch(resetBookingDetails());
  };

  useEffect(() => {
    return () => {
      dispatch(resetBookingDetails());
    };
  }, []);

  useGetMealPlanRate(roomTypes, "EP");

  // Fetch companies with optional search text
  const fetchCompanies = async (searchText = "") => {
    if (!hotelDetails?.id) return [];
    const payload = { hotelId: hotelDetails.id };
    if (searchText) payload.searchText = searchText;
    const res = await getPartyService(payload, '/party');
    return res.data || [];
  };

  // Add company API wrapper
  const handleAddCompany = async (companyName) => {
    if (!hotelDetails?.id) throw new Error("Hotel ID missing");

    const payload = JSON.parse(JSON.stringify(partyObj));
    payload.displayName = companyName;
    payload.name = companyName; 
    payload.partyType = 'source';
    payload.hotelId = hotelDetails.id;

    const res = await postPartyService(payload, '/party');
    return res.data; // Should be {id, name}
  };

  // Save handler for modal
  const handleSaveCompany = async (companyObj) => {
    // Create a copy of bookingDetails
    const updatedBooking = { ...bookingDetails };
    updatedBooking.partyId = companyObj.id;
    updatedBooking.source = companyObj.displayName || companyObj.name;
    updatedBooking.companyBookingId = companyObj.companyBookingId || null;
    // Call booking update API
    const response = await modifyBooking(updatedBooking, `/bookings/${updatedBooking.id}`);
    setEditCompanyModalOpen(false);
    // Update bookingDetails in reducer with the updated response
    if (response && response.data) {
      dispatch(updateBookingDetailsEditable(response.data));
    }
  };

  return (
    <Drawer
      headerStyle={{ padding: 0, margin: 0 }}
      bodyStyle={{ padding: 0, margin: 0 }} // Remove padding and margin
      closeIcon={<></>}
      closable={false}
      onClose={handleClose}
      open={showMainBookingDetailsDrawer?.visible}
      width={bookingDetailsDrawerWidth}
      title={
        !loadingDetails && (
          <header className="flex justify-between py-4 px-3  w-[100%] bg-[#F9FBFC] ">
            <div className="text-[1rem] font-outfit text-[#4D4D4D]">
              <div className="flex items-center gap-2">
                <span className=" font-[500] text-[1rem] ">Booking Id:</span>

                <span className=" font-[600]  text-[1rem]">
                  {bookingDetails?.bookingId}
                </span>
              </div>

              <div className="flex items-center gap-2">
                <span className=" font-[500] text-[1]">Company:</span>

                <span
                  className="font-[600] text-[0.875rem] text-[#007FFF] capitalize cursor-pointer hover:underline"
                  onClick={() => setEditCompanyModalOpen(true)}
                  title="Edit Company"
                >
                  {bookingDetails?.source ?? "Walk In"} - {bookingDetails?.companyBookingId ?? ""}
                </span>
              </div>
      <EditCompanyModal
        visible={editCompanyModalOpen}
        onClose={() => setEditCompanyModalOpen(false)}
        onSave={handleSaveCompany}
        fetchCompanies={fetchCompanies}
        addCompany={handleAddCompany}
        initialCompanyName={bookingDetails?.source ?? null}
        initialCompanyId={bookingDetails?.partyId ?? null}
        initialCompanyBookingId={bookingDetails?.companyBookingId ?? null}
      />
            </div>

            <div className="flex justify-center gap-3 items-center pr-5">
              <Button
                onClick={handleFolio}
                className="bg-[#526EFF] bg-opacity-5 border-none rounded-lg text-[#007FFF] font-medium text-[14px]"
              >
                Bills
              </Button>

              <Button
                onClick={handleGrc}
                className="bg-[#526EFF] bg-opacity-5 border-none rounded-lg text-[#007FFF] font-medium text-[14px]"
              >
                GRC
              </Button>

              <CloseOutlined
                onClick={handleClose}
                className=" customCloseIconContainer"
              />
            </div>
          </header>
        )
      }
      mask={true}
    >
      {loadingDetails || !showMainBookingDetailsDrawer?.visible ? (
        <div className="w-[100%] h-[100%] flex justify-center items-center">
          <CircularProgress size={32} sx={{ color: "#007FFF" }} />
        </div>
      ) : (
        <div
          className="flex w-[100%]  flex-col gap-3 pb-10 mb-10  
        no-scrollbar::-webkit-scrollbar no-scrollbar p-2"
        >
          <CheckIn
            className="w-[90%]"
            bookingType={bookingType}
            isBookingAlterationAllowed={isBookingAlterationAllowed}
          />

          <div className="w-[100%] flex gap-[12px] items-start mb-[50px]">
            {isBookingAlterationAllowed ? <AddOnsContainer /> : <></>}

            <ExtraOption
              bookingType={bookingType}
              isBookingAlterationAllowed={isBookingAlterationAllowed}
            />
          </div>

          <Footer
            bookingType={bookingType}
            isBookingAlterationAllowed={isBookingAlterationAllowed}
          />
        </div>
      )}
    </Drawer>
  );
};

export default MainBookingDetailsDrawer;
